import { DataTable } from "@/components/admin/data-table";
import { ReferenceField } from "@/components/admin/reference-field";
import { ReferenceManyCount } from "@/components/admin/reference-many-count";
import { ReferenceManyField } from "@/components/admin/reference-many-field";
import { TextField } from "@/components/admin/text-field";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useIsMobile } from "@/hooks/use-mobile";
import { ArrowLeft, FileText, Pencil } from "lucide-react";
import { RecordRepresentation, ShowBase, useShowContext } from "ra-core";
import { useState } from "react";
import { Link } from "react-router";

import { ActivityTimeline } from "../activities/ActivityTimeline";
import { CompanyAvatar } from "../companies/CompanyAvatar";
import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import { MobileBackButton } from "../misc/MobileBackButton";
import { NoteCreate, NotesIterator, NotesIteratorMobile } from "../notes";
import { NoteCreateSheet } from "../notes/NoteCreateSheet";
import type { Contact } from "../types";
import { Avatar } from "./Avatar";
import { ContactAside } from "./ContactAside";
import { ContactBackgroundInfo } from "./ContactBackgroundInfo";
import { ContactEditSheet } from "./ContactEditSheet";
import { ContactPersonalInfo } from "./ContactPersonalInfo";
import { ContactTasksList } from "./ContactTasksList";
import { TagsListEdit } from "./TagsListEdit";

export const ContactShow = () => {
  const isMobile = useIsMobile();

  return (
    <ShowBase
      queryOptions={{
        onError: isMobile
          ? () => {
              {
                /** Disable error notification as the content handles offline */
              }
            }
          : undefined,
      }}
    >
      <div className="h-full overflow-y-auto scrollbar-hide pb-10 pr-4">
        {isMobile ? <ContactShowContentMobile /> : <ContactShowContent />}
      </div>
    </ShowBase>
  );
};

const ContactShowContentMobile = () => {
  const { record, isPending } = useShowContext<Contact>();
  const [noteCreateOpen, setNoteCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  if (isPending || !record) return null;

  return (
    <>
      {/* We need to repeat the note creation sheet here to support the note 
      create button that is rendered when there are no notes. */}
      <NoteCreateSheet
        open={noteCreateOpen}
        onOpenChange={setNoteCreateOpen}
        contact_id={record.id}
      />
      <ContactEditSheet
        open={editOpen}
        onOpenChange={setEditOpen}
        contactId={record.id}
      />
      <MobileHeader>
        <MobileBackButton />
        <div className="flex flex-1">
          <Link to="/contacts">
            <h1 className="text-xl font-semibold">
              <RecordRepresentation />
            </h1>
          </Link>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="rounded-full"
          onClick={() => setEditOpen(true)}
        >
          <Pencil className="size-5" />
          <span className="sr-only">Edit record</span>
        </Button>
      </MobileHeader>
      <MobileContent>
        <div className="mb-6">
          <div className="flex items-center mb-4">
            <Avatar />
            <div className="mx-3 flex-1">
              <h2 className="text-2xl font-bold">
                <RecordRepresentation />
              </h2>
              <div className="text-sm text-muted-foreground">
                {record.title}
                {record.title && record.company_id != null && " at "}
                {record.company_id != null && (
                  <ReferenceField
                    source="company_id"
                    reference="companies"
                    link="show"
                  >
                    <TextField source="name" className="underline" />
                  </ReferenceField>
                )}
              </div>
            </div>
            <div>
              <ReferenceField
                source="company_id"
                reference="companies"
                link="show"
                className="no-underline"
              >
                <CompanyAvatar />
              </ReferenceField>
            </div>
          </div>
        </div>

        <Tabs defaultValue="activity" className="w-full">
          <TabsList className="grid w-full grid-cols-4 h-10">
            <TabsTrigger value="activity">Activity</TabsTrigger>
            <TabsTrigger value="notes">Notes</TabsTrigger>
            <TabsTrigger value="tasks">
              <ReferenceManyCount
                target="contact_id"
                reference="tasks"
                filter={{ "done_date@is": null }}
              />{" "}
              Tasks
            </TabsTrigger>
            <TabsTrigger value="details">Details</TabsTrigger>
          </TabsList>

          <TabsContent value="activity" className="mt-2 h-[600px]">
            <ActivityTimeline resource="contacts" recordId={record.id} />
          </TabsContent>

          <TabsContent value="notes" className="mt-2">
            <ReferenceManyField
              target="contact_id"
              reference="contact_notes"
              sort={{ field: "date", order: "DESC" }}
              empty={
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <p className="text-muted-foreground mb-4">No notes yet</p>
                  <Button
                    variant="outline"
                    onClick={() => setNoteCreateOpen(true)}
                  >
                    Add note
                  </Button>
                </div>
              }
              loading={false}
              error={false}
              queryOptions={
                {
                  onError: () => {
                    /** override to hide notification as error case is handled by NotesIteratorMobile */
                  },
                } as any // fixme: remove once https://github.com/marmelab/react-admin/pull/11166 is released
              }
            >
              <NotesIteratorMobile contactId={record.id} showStatus />
            </ReferenceManyField>
          </TabsContent>

          <TabsContent value="tasks" className="mt-4">
            <ContactTasksList />
          </TabsContent>

          <TabsContent value="details" className="mt-4">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold">Personal info</h3>
                <Separator />
                <div className="mt-3">
                  <ContactPersonalInfo />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold">Background info</h3>
                <Separator />
                <div className="mt-3">
                  <ContactBackgroundInfo />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold">Tags</h3>
                <Separator />
                <div className="mt-3">
                  <TagsListEdit />
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </MobileContent>
    </>
  );
};

const ContactShowContent = () => {
  const { record, isPending } = useShowContext<Contact>();
  if (isPending || !record) return null;

  return (
    <div className="mt-2 mb-2 flex flex-col gap-4">
      {/* Top Left Back Button */}
      <div className="flex justify-start">
        <Button asChild variant="outline" size="sm" className="gap-1.5 bg-[#00a68d]/10 hover:bg-[#00a68d]/20 text-[#00a68d] hover:text-[#00947e] border border-[#00a68d]/20 hover:border-[#00a68d]/30 font-semibold cursor-pointer h-9 px-4 rounded-lg">
          <Link to="/contacts" className="flex items-center gap-1.5">
            <ArrowLeft className="w-4 h-4 text-[#00a68d]" /> Back to Leads
          </Link>
        </Button>
      </div>

      <div className="flex gap-8">
      <div className="flex-1 space-y-6">
        <Card>
          <CardContent>
            <div className="flex">
              <Avatar />
              <div className="ml-2 flex-1">
                <h5 className="text-xl font-semibold">
                  <RecordRepresentation />
                </h5>
                <div className="inline-flex text-sm text-muted-foreground">
                  {record.title}
                  {record.title && record.company_id != null && " at "}
                  {record.company_id != null && (
                    <ReferenceField
                      source="company_id"
                      reference="companies"
                      link="show"
                    >
                      &nbsp;
                      <TextField source="name" />
                    </ReferenceField>
                  )}
                </div>
              </div>
              <div>
                <ReferenceField
                  source="company_id"
                  reference="companies"
                  link="show"
                  className="no-underline"
                >
                  <CompanyAvatar />
                </ReferenceField>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="activity" className="w-full">
          <TabsList>
            <TabsTrigger value="activity">Activity</TabsTrigger>
            <TabsTrigger value="notes">Notes</TabsTrigger>
            <TabsTrigger value="docs">Documents</TabsTrigger>
          </TabsList>
          
          <TabsContent value="activity" className="mt-4 h-[600px]">
            <ActivityTimeline resource="contacts" recordId={record.id} />
          </TabsContent>

          <TabsContent value="notes" className="mt-4">
            <Card>
              <CardContent className="pt-6">
                <ReferenceManyField
                  target="contact_id"
                  reference="contact_notes"
                  sort={{ field: "date", order: "DESC" }}
                  empty={
                    <NoteCreate reference="contacts" showStatus className="mt-4" />
                  }
                >
                  <NotesIterator reference="contacts" showStatus />
                </ReferenceManyField>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="docs" className="mt-4 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-4 h-4 text-primary" /> Quotations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ReferenceManyField reference="quotations" target="customer_id">
                  <DataTable>
                    <DataTable.Col source="reference" label="Ref" />
                    <DataTable.Col source="status" label="Status" />
                    <DataTable.NumberCol source="total_amount" label="Amount" options={{ style: 'currency', currency: 'INR' }} />
                    <DataTable.Col source="date" label="Date" />
                  </DataTable>
                </ReferenceManyField>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-4 h-4 text-primary" /> Proforma Invoices
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ReferenceManyField reference="proforma_invoices" target="customer_id">
                  <DataTable>
                    <DataTable.Col source="reference" label="Ref" />
                    <DataTable.Col source="status" label="Status" />
                    <DataTable.NumberCol source="total_amount" label="Amount" options={{ style: 'currency', currency: 'INR' }} />
                    <DataTable.Col source="date" label="Date" />
                  </DataTable>
                </ReferenceManyField>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-4 h-4 text-primary" /> Invoices
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ReferenceManyField reference="customer_invoices" target="customer_id">
                  <DataTable>
                    <DataTable.Col source="reference" label="Ref" />
                    <DataTable.Col source="status" label="Status" />
                    <DataTable.NumberCol source="total_amount" label="Amount" options={{ style: 'currency', currency: 'INR' }} />
                    <DataTable.Col source="date" label="Date" />
                  </DataTable>
                </ReferenceManyField>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      <ContactAside link="show" />
      </div>
    </div>
  );
};
